import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags/index.ts'
import * as nodes from '/vercel/path0/markdoc/nodes/index.ts'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: The Covalent API Extends Support to Avalanche Bringing Unparalleled Data Accessibility to the Platform\ndate: 2021-03-16T04:25:15-04:00\ncategories: [\"ecosystem\"]\ndescription: Covalent Announces Integration with Avalanche\nauthor: niall\nsubbrand_image: https://www.covalenthq.com/static/images/blog/202103/avalanche.jpg\ntype: legacy\n---\n\n\n**VANCOUVER, March 16th, 2021** - Covalent is happy to announce a technical integration with Avalanche, an open-source platform for launching decentralized applications and enterprise blockchain deployments in one interoperable, highly scalable ecosystem.\n\n\n\nAvalanche is the first smart contracts platform that confirms transactions instantly, supports the entirety of the Ethereum development toolkit, and enables millions of independent validators to participate as full block producers.\n\nThe Avalanche protocol employs a novel approach to consensus to achieve its strong safety guarantees, quick finality, and high-throughput, without compromising decentralization. As a result, developers have been drawn to Avalanche to build powerful, reliable, and secure applications and custom blockchain networks.\n\nSince the launch of the [Avalanche-Ethereum Bridge](https://aeb.xyz) (AEB) on February 8, 2021, smart contract activity on Avalanche has boomed, with transactions increasing by 972% to over 580,000, and unique wallets increasing by 1,681% to top 38,000.\n\nAs with all blockchain networks, data accessibility remains an issue given the nature of the data created with each transaction.\n\nHowever, through this integration, Avalanche is expanding the availability of data on the platform and simplifying the experience for developers who need reliable, high-quality data. Using the Covalent API, developers can pull Avalanche mainnet and Fuji testnest data. You can query either network via the unified API by changing the chainId parameter. This seamless experience significantly enhances the Avalanche developer experience.\n\nSimilar to Avalanche, Covalent places significant emphasis on developer experience. Covalent offers a single API that allows you to pull detailed, granular blockchain transaction data from multiple blockchains with no code. This means no complicated SQL queries, no subgraph development and maintenance, and no need to invest in highly-skilled developers to simply retrieve blockchain data. And not to mention, the blockchain data is extensive covering:\n\n- Token balances per address\n- Historical transactions per address\n- Historical prices by address and ticker symbol\n- Decoded log events\n\n## Quote from Ganesh, Covalent's CEO\n\n\n \n \"\n Avalanche is building an excellent platform with developers at the heart of it. This integration with Covalent significantly enhances the developer toolkit on Avalanche. The breadth and depth of Avalanche data now available will undoubtedly lead to even more powerful applications being built across the network.\n \n\n\n## Quote from John Wu, President at Ava Labs\n\n\n \n \"\n Reliable, high-quality data is vital to building performant DeFi applications. With Covalent's data on Avalanche, developers building on the platform will be able to build and launch applications faster than ever.\n \n\n\n## Resources\n\nFor more information on how to query the Avalanche network, visit: [https://www.covalenthq.com/docs/networks/avalanche](https://www.covalenthq.com/docs/networks/avalanche).\n\nOtherwise, information regarding the Covalent API and the extensive list of endpoints can be found here: [https://www.covalenthq.com/docs/api](https://www.covalenthq.com/docs/api).\n\n\n## About Avalanche\nAvalanche is a highly-scalable, open-source platform for launching decentralized applications. Developers who build on Avalanche can easily create powerful, reliable, and secure applications and custom blockchain networks with complex rulesets or build on existing private or public subnets.\n\n\n\n\n\n";
const filepath = "/blog/avalanche-announcement.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
